import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, FieldLabel, FieldWrapper, FlexBox, IconButton, InputText, Tooltip, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { useBreakpointValue } from 'utils/mediaQuery';
import { getAuthorizationKeyMessage, getEmptyInputMessage, getKeyValidationMessage, } from '../../utils/validateMessages';
import styles from './customHeaders.module.scss';
export const CustomHeaders = ({ onChange, value, disabled, }) => {
    const [customHeaders, setCustomHeaders] = useState([]);
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const handleOnChange = (headers) => {
        onChange(headers.reduce((obj, current) => (Object.assign(Object.assign({}, obj), { [current.key.toLowerCase()]: current.value })), {}));
    };
    const handleCustomHeaderChange = (key, value, indexToChange) => {
        const changedHeaders = customHeaders.map((header, index) => index === indexToChange
            ? {
                key,
                value,
            }
            : header);
        setCustomHeaders(changedHeaders);
        handleOnChange(changedHeaders);
    };
    const handleCustomHeaderRemoval = (indexToRemove) => {
        const changedHeaders = customHeaders.filter((_header, index) => index !== indexToRemove);
        setCustomHeaders(changedHeaders);
        handleOnChange(changedHeaders);
    };
    const handleCustomHeaderAddition = () => {
        const changedHeaders = [
            ...customHeaders,
            {
                key: '',
                value: '',
            },
        ];
        setCustomHeaders(changedHeaders);
        handleOnChange(changedHeaders);
    };
    useEffect(() => {
        if (value && Object.keys(value).length > 0 && customHeaders.length === 0) {
            setCustomHeaders(Object.entries(value).map(([key, value]) => ({ key, value })));
            onChange(value);
        }
    }, [value]);
    useEffect(() => {
        if (customHeaders.length === 0) {
            onChange(undefined);
        }
    }, [customHeaders]);
    return (_jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "200", margin: "0 0 300 0" }, { children: [_jsx(FlexBox, { children: _jsx(FieldLabel, Object.assign({ helpIcon: {
                        dark: true,
                        title: 'Custom headers',
                        text: 'You can specify your own custom headers as key/value pair. However, the key `authorization` is not allowed as custom header (use the input field above for that).',
                    } }, { children: "Custom headers" })) }), _jsx(FlexBox, Object.assign({ flexDirection: "column", gap: "200" }, { children: customHeaders.length > 0 ? (customHeaders.map(({ key, value }, headerIndex) => (_jsxs(FlexBox, Object.assign({ padding: "300", className: styles.customHeaders, flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? '300' : '200' }, { children: [_jsx(FieldWrapper, Object.assign({ required: true, disabled: disabled, label: "Key", helpIcon: {
                                dark: true,
                                title: 'Rules when adding custom headers',
                                text: (_jsxs("ul", { children: [_jsxs("li", { children: ["No spaces are allowed in the ", _jsx("i", { children: "key" }), " field"] }), _jsxs("li", { children: ["Don't use special unicode characters in the", ' ', _jsx("i", { children: "key" }), " field"] }), _jsxs("li", { children: ["Uppercase ", _jsx("i", { children: "key" }), " values will be transformed to lowercase"] }), _jsxs("li", { children: ["The key ", _jsx("i", { children: "authorization" }), " is not allowed here - please use the ", _jsx("i", { children: "Authorization header" }), " input instead."] })] })),
                            }, meta: {
                                error: getEmptyInputMessage(key) ||
                                    getAuthorizationKeyMessage(key) ||
                                    getKeyValidationMessage(key),
                                warning: /(.*[A-Z].*)/.test(key)
                                    ? 'Uppercase characters will be saved with lowercase characters.'
                                    : undefined,
                            } }, { children: _jsx(InputText, { disabled: disabled, status: getEmptyInputMessage(key) ? 'error' : undefined, value: key, onChange: (event) => {
                                    const changedKey = event.target.value;
                                    handleCustomHeaderChange(changedKey, value, headerIndex);
                                }, placeholder: "Key" }) }), `${headerIndex}-key`), _jsx(FieldWrapper, Object.assign({ required: true, disabled: disabled, label: "Value", meta: {
                                // We want to check the value field only when the 'key' is not empty
                                error: !getEmptyInputMessage(key)
                                    ? getEmptyInputMessage(value)
                                    : undefined,
                            } }, { children: _jsx(InputText, { disabled: disabled, status: !getEmptyInputMessage(key) &&
                                    Boolean(getEmptyInputMessage(value))
                                    ? 'error'
                                    : undefined, value: value, onChange: (event) => {
                                    const changedValue = event.target.value;
                                    handleCustomHeaderChange(key, changedValue, headerIndex);
                                }, placeholder: "Value" }) }), `${headerIndex}-value`), _jsx(FlexBox, Object.assign({ alignItems: "center", padding: isSmallScreen ? undefined : '0 0 0 300', justifyContent: isSmallScreen ? 'end' : undefined }, { children: _jsx(Tooltip, Object.assign({ dark: true, content: "Remove header" }, { children: _jsx(IconButton, { disabled: disabled, variant: "secondary", icon: "trash", onClick: () => handleCustomHeaderRemoval(headerIndex) }) })) }))] }), headerIndex)))) : (_jsx(FlexBox, Object.assign({ padding: "250 400", className: classnames(styles.noCustomHeadersMessage, styles.customHeaders) }, { children: "No custom headers added" }))) })), _jsx(FlexBox, Object.assign({ alignItems: "flex-start", margin: "0 0 100 0" }, { children: _jsx(Button, Object.assign({ disabled: disabled, icon: "plus", variant: "secondary", onClick: () => handleCustomHeaderAddition() }, { children: "Add custom header" })) }))] })));
};
